import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';
import { getBulkPresence } from '../../shared/redis';
import bcrypt from 'bcryptjs';
import { Prisma } from '@prisma/client';

export async function listUsers(workspaceId?: string, currentUserId?: string, limit: number = 50, cursor?: string) {
  const where: any = {};
  if (currentUserId) {
    const userRoles = await prisma.userRole.findMany({
      where: { userId: currentUserId },
      include: { role: true },
    });
    const isAdmin = userRoles.some(ur => ur.role.name === 'admin' || ur.role.name === 'super_admin');

    if (!isAdmin) {
      const isRestricted = userRoles.some(ur => ur.role.name === 'department_restricted');
      const currentUser = await prisma.user.findUnique({ 
        where: { id: currentUserId },
        include: { department: true }
      });
      
      const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
      const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;
      
      if (!isCommonDeptUser) {
        if (currentUser?.companyId) {
          if (isRestricted) {
            where.OR = [
              { id: currentUserId },
              {
                companyId: currentUser.companyId,
                departmentId: currentUser.departmentId,
              },
              {
                department: {
                  name: { in: commonDeptNames }
                }
              },
              { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
            ];
          } else {
            where.OR = [
              { id: currentUserId },
              { companyId: currentUser.companyId },
              {
                department: {
                  name: { in: commonDeptNames }
                }
              },
              { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
            ];
          }
        } else {
          where.OR = [
            { id: currentUserId },
            {
              department: {
                name: { in: commonDeptNames }
              }
            },
            { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
          ];
        }
      }
    }
  }

  const users = await prisma.user.findMany({
    where,
    take: limit,
    ...(cursor ? { skip: 1, cursor: { id: cursor } } : {}),
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, isActive: true, createdAt: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } }
    },
    orderBy: { displayName: 'asc' },
  });

  const presenceMap = await getBulkPresence(users.map((u: any) => u.id));
  return users.map((u: any) => ({
    ...u,
    presence: presenceMap[u.id] || 'offline'
  }));
}

export async function getUserById(id: string) {
  const user = await prisma.user.findUnique({
    where: { id },
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, createdAt: true, isActive: true, status: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } }
    },
  });
  if (!user) throw new AppError(404, 'User not found');
  const presenceMap = await getBulkPresence([user.id]);
  return { ...user, presence: presenceMap[user.id] || 'offline' };
}

export async function getUserByUsername(username: string) {
  const user = await prisma.user.findUnique({
    where: { username },
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, createdAt: true, isActive: true, status: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } }
    },
  });
  if (!user) throw new AppError(404, 'User not found');
  const presenceMap = await getBulkPresence([user.id]);
  return { ...user, presence: presenceMap[user.id] || 'offline' };
}

export async function createUser(data: any) {
  const existingEmail = await prisma.user.findUnique({ where: { email: data.email } });
  if (existingEmail) throw new AppError(400, 'Email is already in use');

  const existingUsername = await prisma.user.findUnique({ where: { username: data.username } });
  if (existingUsername) throw new AppError(400, 'Username is already taken');

  const passwordHash = await bcrypt.hash(data.password, 12);

  return prisma.$transaction(async (tx: Prisma.TransactionClient) => {
    const user = await tx.user.create({
      data: {
        email: data.email,
        username: data.username,
        displayName: data.displayName,
        passwordHash,
        avatarUrl: data.avatarUrl,
        companyId: data.companyId || null,
        departmentId: data.departmentId || null,
        position: data.position || null,
        employeeId: data.employeeId || null,
      },
    });

    if (data.role) {
      let roleRecord = await tx.role.findUnique({ where: { name: data.role } });
      if (!roleRecord) {
        roleRecord = await tx.role.create({ data: { name: data.role } });
      }
      await tx.userRole.create({
        data: {
          userId: user.id,
          roleId: roleRecord.id,
        },
      });
    }

    const { passwordHash: _, ...userWithoutPassword } = user;
    return userWithoutPassword;
  });
}

export async function updateUser(id: string, data: any) {
  const user = await prisma.user.findUnique({ where: { id } });
  if (!user) throw new AppError(404, 'User not found');

  const updateData: any = {
    displayName: data.displayName,
    avatarUrl: data.avatarUrl,
    status: data.status,
    isActive: data.isActive,
    email: data.email,
    username: data.username,
    companyId: data.companyId !== undefined ? data.companyId : undefined,
    departmentId: data.departmentId !== undefined ? data.departmentId : undefined,
    position: data.position !== undefined ? data.position : undefined,
    employeeId: data.employeeId !== undefined ? data.employeeId : undefined,
  };

  if (data.password) {
    updateData.passwordHash = await bcrypt.hash(data.password, 12);
  }

  return prisma.user.update({
    where: { id },
    data: updateData,
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, isActive: true, status: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } }
    },
  });
}

export async function updateProfile(userId: string, data: { displayName?: string; avatarUrl?: string | null; status?: string | null; workLocation?: string | null }) {
  return prisma.user.update({
    where: { id: userId },
    data,
    select: { id: true, email: true, username: true, displayName: true, avatarUrl: true, status: true, workLocation: true },
  });
}

export async function activateUser(userId: string) {
  return prisma.user.update({ where: { id: userId }, data: { isActive: true } });
}

export async function deactivateUser(userId: string) {
  return prisma.user.update({ where: { id: userId }, data: { isActive: false } });
}

export async function deleteUser(userId: string) {
  return prisma.user.delete({ where: { id: userId } });
}

export async function searchUsers(query: string, limit: number = 20, currentUserId?: string) {
  const where: any = {
    OR: [
      { displayName: { contains: query, mode: 'insensitive' } },
      { username: { contains: query, mode: 'insensitive' } },
      { email: { contains: query, mode: 'insensitive' } },
    ],
  };

  if (currentUserId) {
    const userRoles = await prisma.userRole.findMany({
      where: { userId: currentUserId },
      include: { role: true },
    });
    const isAdmin = userRoles.some(ur => ur.role.name === 'admin' || ur.role.name === 'super_admin');
    const isRestricted = userRoles.some(ur => ur.role.name === 'department_restricted');

    if (!isAdmin) {
      const isRestricted = userRoles.some(ur => ur.role.name === 'department_restricted');
      const currentUser = await prisma.user.findUnique({ 
        where: { id: currentUserId },
        include: { department: true }
      });
      
      const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
      const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;
      
      if (!isCommonDeptUser) {
        if (currentUser?.companyId) {
          if (isRestricted) {
            where.AND = [
              {
                OR: [
                  { id: currentUserId },
                  {
                    companyId: currentUser.companyId,
                    departmentId: currentUser.departmentId,
                  },
                  {
                    department: {
                      name: { in: commonDeptNames }
                    }
                  },
                  { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
                ]
              }
            ];
          } else {
            where.AND = [
              {
                OR: [
                  { id: currentUserId },
                  { companyId: currentUser.companyId },
                  {
                    department: {
                      name: { in: commonDeptNames }
                    }
                  },
                  { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
                ]
              }
            ];
          }
        } else {
          where.AND = [
            {
              OR: [
                { id: currentUserId },
                {
                  department: {
                    name: { in: commonDeptNames }
                  }
                },
                { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
              ]
            }
          ];
        }
      }
    }
  }

  const users = await prisma.user.findMany({
    where,
    select: { 
      id: true, username: true, displayName: true, avatarUrl: true, presence: true, isActive: true, email: true, createdAt: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } }
    },
    take: limit,
  });

  const presenceMap = await getBulkPresence(users.map((u: any) => u.id));
  return users.map((u: any) => ({
    ...u,
    presence: presenceMap[u.id] || 'offline'
  }));
}

export async function getUserPresenceBulk(userIds: string[]) {
  return getBulkPresence(userIds);
}
